/**
 * Courses Store
 *
 * State management for courses with offline support
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { coursesApi } from '@/api/courses.api'
import { db } from '@/db'
import type { CourseDto } from '@/types/api.types'

export const useCoursesStore = defineStore('courses', () => {
  // State
  const courses = ref<CourseDto[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetched = ref<Date | null>(null)

  // Getters
  const courseCount = computed(() => courses.value.length)

  const getById = computed(() => {
    return (id: number) => courses.value.find((c) => c.id === id)
  })

  const sortedByTitle = computed(() => {
    return [...courses.value].sort((a, b) => a.title.localeCompare(b.title))
  })

  // Actions

  /**
   * Fetch all courses
   * @param forceRefresh - Skip cache and fetch from API
   */
  async function fetchAll(forceRefresh = false): Promise<void> {
    // Offline + data already in memory → skip API & IndexedDB roundtrip
    if (!navigator.onLine && courses.value.length > 0 && !forceRefresh) return

    error.value = null

    try {
      // Try to load from cache first (don't show loading if we have data).
      // Skip cache after store reset (lastFetched === null) to prevent showing
      // a previous user's data after re-login.
      if (!forceRefresh && lastFetched.value !== null) {
        const cached = await db.courses.toArray()
        if (cached.length > 0) {
          courses.value = cached
          // Continue to fetch fresh data in background (no loading indicator)
        } else {
          isLoading.value = true
        }
      } else {
        isLoading.value = true
      }

      // Fetch from API
      const data = await coursesApi.getAll()
      courses.value = data
      lastFetched.value = new Date()

      // Update cache
      await db.transaction('rw', db.courses, async () => {
        await db.courses.clear()
        await db.courses.bulkAdd(data)
      })
    } catch (e) {
      // If API fails, try cache
      if (courses.value.length === 0) {
        const cached = await db.courses.toArray()
        if (cached.length > 0) {
          courses.value = cached
          console.warn('[CoursesStore] Loaded from cache after API failure')
        } else {
          error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Kurse'
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch single course by ID
   * @param id - Course ID
   * @param forceRefresh - Skip cache and fetch from API
   */
  async function fetchById(id: number, forceRefresh = false): Promise<CourseDto | null> {
    try {
      // Check local first (unless forcing refresh)
      if (!forceRefresh) {
        let course = courses.value.find((c) => c.id === id)
        if (course) return course

        // Check cache
        course = await db.courses.get(id)
        if (course) {
          courses.value.push(course)
          return course
        }
      }

      // Fetch from API
      const data = await coursesApi.getById(id)
      if (data) {
        // Update or add to local state
        const existingIndex = courses.value.findIndex((c) => c.id === id)
        if (existingIndex >= 0) {
          courses.value[existingIndex] = data
        } else {
          courses.value.push(data)
        }
        await db.courses.put(data)
      }
      return data
    } catch (e) {
      console.error('[CoursesStore] Failed to fetch course:', e)
      return null
    }
  }

  /**
   * Clear store and cache
   */
  async function $reset(): Promise<void> {
    courses.value = []
    isLoading.value = false
    error.value = null
    lastFetched.value = null
    await db.courses.clear()
  }

  return {
    // State
    courses,
    isLoading,
    error,
    lastFetched,
    // Getters
    courseCount,
    getById,
    sortedByTitle,
    // Actions
    fetchAll,
    fetchById,
    $reset,
  }
})
